import React, { useEffect, useState } from "react";
import {
    Box, Paper, Typography, Button, Chip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { getPlans, assignPlan } from "../api/planApi";
import { createRazorpayOrder, verifyRazorpayPayment } from "../api/razorpayApi";
import { toast, ToastContainer } from "react-toastify";
import LogoLoader from "../components/Common/LogoLoader";
import { useAuth } from "../context/AuthContext";

const FEATURE_LABELS = {
    lead_generation: "Lead Generation Bot",
    customer_support: "Customer Support Bot",
    live_chat: "Live Chat",
    canned_responses: "Canned Responses",
    analytics: "Chatbot Analytics",
    template_message_analytics: "Template Message Analytics",
    webhook_integrations: "Webhook Integrations",
    google_sheets: "Google Sheets Integration",
    crm_standard: "CRM (Standard)",
    email_otp: "Email OTP Validations",
    lead_notifications: "Lead Email Notifications",
    hot_lead_notifications: "Hot Lead Notifications",
    auto_email_followup: "Auto Email Followup",
    multilingual: "Multi-Lingual (2 Languages)",
    ai_bot: "AI Bot (Self Setup)",
    ai_speech: "AI Speech Answering",
    voice_intent_mapping: "Voice Enabled Intent Mapping",
    csv_export: "CSV Chats Export",
    contacts_dashboard: "Contacts Dashboard",
    whatsapp_catalog: "WhatsApp Catalog",
    whatsapp_ecommerce: "WhatsApp E-Commerce",
    "2fa": "2 Factor Authentication",
    lead_data_masking: "Lead Data Masking",
};

const Plans = () => {
    const { user, fetchMe } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upgrading, setUpgrading] = useState(null);

    const userPlan = user?.plan || "free";
    const userEmail = user?.email || "";
    const isTrial = user?.isTrial;
    const trialEndsAt = user?.trialEndsAt;

    useEffect(() => {
        getPlans()
            .then(setPlans)
            .catch(console.error)
            .finally(() => setLoading(false));

        const debugData = localStorage.getItem("debug_plan");
        if (debugData) {
            console.log("**** PLAN DEBUG (from localStorage) ****", JSON.parse(debugData));
            localStorage.removeItem("debug_plan");
        }
    }, []);

    const handleUpgrade = async (plan) => {
        if (plan.price <= 0) {
            setUpgrading(plan._id);
            try {
                await assignPlan(plan._id);
                await fetchMe();
                toast.success(`Upgraded to ${plan.name}!`);
            } catch (err) {
                toast.error(err?.response?.data?.message || "Upgrade failed");
            } finally {
                setUpgrading(null);
            }
            return;
        }

        setUpgrading(plan._id);
        try {
            const order = await createRazorpayOrder(plan._id);
            if (!order.keyId) {
                toast.error("Payment gateway not configured (missing key)");
                setUpgrading(null);
                return;
            }
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => {
                const options = {
                    key: order.keyId,
                    amount: order.amount,
                    currency: order.currency,
                    name: "Talktokam",
                    description: `${order.planName} Plan`,
                    order_id: order.orderId,
                    handler: async (response) => {
                        try {
                            const verifyRes = await verifyRazorpayPayment({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                planId: plan._id,
                            });
                            await fetchMe();
                            if (verifyRes._debug) {
                                localStorage.setItem("debug_plan", JSON.stringify(verifyRes._debug));
                            }
                            toast.success(`Upgraded to ${plan.name}!`);
                            // setTimeout(() => window.location.href = "/app/plans", 1000);
                        } catch (err) {
                            const msg = err?.response?.data?.message || err?.message || "Payment verification failed";
                            toast.error(msg);
                        } finally {
                            setUpgrading(null);
                        }
                    },
                    modal: {
                        ondismiss: () => setUpgrading(null),
                    },
                    prefill: { email: userEmail },
                    theme: { color: "#6366f1" },
                };
                const rzp = new window.Razorpay(options);
                rzp.on("payment.failed", () => {
                    toast.error("Payment failed");
                    setUpgrading(null);
                });
                rzp.open();
            };
            document.body.appendChild(script);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to initiate payment");
            setUpgrading(null);
        }
    };

    if (loading) {
        return <LogoLoader message="Loading plans..." />;
    }

    const limitLabel = (plan, key) => {
        const val = plan.limits[key];
        if (val === -1) return "Unlimited";
        if (key === "languages") return `${val} Language${val > 1 ? "s" : ""}`;
        if (key === "liveAgents") return `${val} Live Agent${val > 1 ? "s" : ""}`;
        if (key === "chatbots") return `${val} Chatbot${val > 1 ? "s" : ""}`;
        if (key === "chatsPerMonth") return `${val.toLocaleString()} Chats/Month`;
        if (key === "waUserChatsPerMonth") return val ? `${val.toLocaleString()} WA Chats/Month` : "No WhatsApp";
        return val;
    };

    const isCurrentPlan = (slug) => userPlan === slug;

    return (
        <Box sx={{
            p: 4, height: "calc(100vh - 70px)", overflowY: "auto",
            background: "linear-gradient(135deg, #f0f4ff 0%, #fdf2f8 50%, #f0f4ff 100%)",
        }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography fontWeight={800} fontSize={28} color="#1f2937" mb={0.5}>
                    Choose Your Plan
                </Typography>
                <Typography variant="body1" color="#6b7280">
                    Scale your chatbot with the right plan for your business
                </Typography>
            </Box>

            <Box sx={{
                display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap",
                maxWidth: 1200, mx: "auto", alignItems: "stretch",
            }}>
                {plans.map((plan) => {
                    const popular = plan.slug === "growth";
                    const current = isCurrentPlan(plan.slug);
                    const trialOnThisPlan = isTrial && plan.slug === "free";

                    return (
                        <Paper key={plan.slug} sx={{
                            width: 280, height: 520, borderRadius: 4, overflow: "hidden", position: "relative",
                            display: "flex", flexDirection: "column",
                            border: current ? "2px solid #6366f1" : popular ? "2px solid #f59e0b" : "1px solid rgba(255,255,255,0.8)",
                            boxShadow: current ? "0 8px 40px rgba(99,102,241,0.2)" : popular ? "0 8px 40px rgba(245,158,11,0.2)" : "0 4px 24px rgba(0,0,0,0.06)",
                            transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                            "&:hover": { transform: "translateY(-6px)", boxShadow: "0 20px 50px rgba(0,0,0,0.12)" },
                            bgcolor: "rgba(255,255,255,0.7)",
                            backdropFilter: "blur(16px)",
                        }}>
                            {popular && (
                                <Chip label="Most Popular" size="small"
                                    sx={{ position: "absolute", top: 12, right: 12, bgcolor: "#f59e0b", color: "#fff", fontWeight: 700, fontSize: 11 }} />
                            )}
                            {current && !trialOnThisPlan && (
                                <Chip label="Your Plan" size="small"
                                    sx={{ position: "absolute", top: 12, left: 12, bgcolor: "#6366f1", color: "#fff", fontWeight: 700, fontSize: 11 }} />
                            )}
                            {trialOnThisPlan && (
                                <Chip label="Trial Active" size="small"
                                    sx={{ position: "absolute", top: 12, left: 12, bgcolor: "#f59e0b", color: "#fff", fontWeight: 700, fontSize: 11 }} />
                            )}

                            <Box sx={{
                                p: 3, textAlign: "center",
                                background: current
                                    ? "linear-gradient(135deg, #6366f1, #8b5cf6)"
                                    : popular
                                        ? "linear-gradient(135deg, #f59e0b, #f97316)"
                                        : "linear-gradient(135deg, #f1f5f9, #e2e8f0)",
                                color: (current || popular) ? "#fff" : "#475569",
                            }}>
                                <Typography fontWeight={700} fontSize={18}>{plan.name}</Typography>
                                <Box sx={{ mt: 1, display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.5 }}>
                                    <Typography fontSize={32} fontWeight={800}>
                                        ₹{plan.price.toLocaleString("en-IN")}
                                    </Typography>
                                    <Typography fontSize={14} sx={{ opacity: 0.8 }}>/month</Typography>
                                </Box>
                                {plan.slug === "enterprise" && (
                                    <Typography fontSize={12} sx={{ opacity: 0.8, mt: 0.5 }}>Custom pricing available</Typography>
                                )}
                                {trialOnThisPlan && (
                                    <Typography fontSize={11} sx={{ opacity: 0.9, mt: 0.5 }}>
                                        Free trial — {trialEndsAt ? Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))) : 14} days remaining
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                                {[
                                    { key: "chatbots", label: "Chatbots" },
                                    { key: "chatsPerMonth", label: "Chats/Month" },
                                    { key: "waUserChatsPerMonth", label: "WhatsApp Chats" },
                                    { key: "liveAgents", label: "Live Agents" },
                                    { key: "languages", label: "Languages" },
                                ].map((l) => (
                                    <Box key={l.key} sx={{ display: "flex", justifyContent: "space-between", py: 0.6 }}>
                                        <Typography variant="body2" color="#6b7280">{l.label}</Typography>
                                        <Typography variant="body2" fontWeight={600} color="#1f2937">{limitLabel(plan, l.key)}</Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ px: 2.5, py: 2, flex: 1, overflowY: "auto" }}>
                                <Typography fontWeight={600} fontSize={12} color="#6b7280" textTransform="uppercase" letterSpacing={0.5} mb={1}>
                                    Features
                                </Typography>
                                {plan.features.map((f) => (
                                    <Box key={f} sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.4 }}>
                                        <CheckIcon sx={{ fontSize: 15, color: "#10b981", mt: 0.3, flexShrink: 0 }} />
                                        <Typography variant="body2" color="#374151">{FEATURE_LABELS[f] || f}</Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ px: 2.5, pb: 3 }}>
                                <Button fullWidth variant={current ? "outlined" : "contained"}
                                    disabled={current || upgrading === plan._id}
                                    onClick={() => !current && handleUpgrade(plan)}
                                    sx={{
                                        textTransform: "none", borderRadius: 2.5, fontWeight: 700, py: 1,
                                        ...(!current ? {
                                            background: current
                                                ? undefined
                                                : popular
                                                    ? "linear-gradient(135deg, #f59e0b, #f97316)"
                                                    : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                            color: "#fff",
                                            boxShadow: "0 4px 15px rgba(0,0,0,0.12)",
                                            "&:hover": {
                                                background: popular ? "linear-gradient(135deg, #f97316, #f59e0b)" : "linear-gradient(135deg, #8b5cf6, #6366f1)",
                                            },
                                        } : {
                                            borderColor: "#6366f1",
                                            color: "#6366f1",
                                        }),
                                    }}>
                                    {current ? "Current Plan" : upgrading === plan._id ? "Upgrading..." : plan.price === 0 ? "Get Started" : "Upgrade"}
                                </Button>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
            <ToastContainer />
        </Box>
    );
};

export default Plans;
